import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { getAppraiserActiveCoverage } from "requests/appraiserCounties";
import { getCountyBoundaries } from "actions/region";
import { getStateList } from "actions/appraiser";
import { Map, MSpinner, RMDButton, MDialogSelectList, MultiSelect, MToolTip, MDialog, MCard } from "components/Shared";
import { Button } from "react-md";
import { formatCounty, mapArrayOfPolygons, sortCounty } from "components/Assignment/Assignment";
import responseHandler from "responseHandler";
import { DATA_TYPE_JSX, DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";
import FauxHeader2 from "components/Shared/FauxHeader/FauxHeader2";
import { requestAppraisalCoverage, sendClickTrackingEmail } from "requests/sales";
import { getCustomersByType } from "requests/customer";
import { BOTH } from "typeConstants/customerTypes";
import RequestCoverage from "components/Coverage/RequestCoverage";
import { allCountiesFromBoundaries } from "selectors/menuItems";

const AppraiserCoverage = props => {
  const [allCoverageOptions, setAllCoverageOptions] = useState(null);
  const [selectedCoverage, setSelectedCoverage] = useState(null);
  const [showStateListPicker, setShowStateListPicker] = useState(false);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedCounties, setSelectedCounties] = useState([]);
  const [mouseOverObject, setMouseOverObject] = useState(null);
  const [lastMapUpdate, setLastMapUpdate] = useState(0);
  const [showRequestCoverageDialog, setShowRequestCoverageDialog] = useState(false);
  const [showKey, setShowKey] = useState(true);
  const customer = useRef([]);
  const sentClickTrackingEmail = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    if (Object.keys(props.countyBoundaries).length == 0) {
      props.getCountyBoundaries();
    }
    if (!props.stateList.length && !props.fetchingStateList) {
      props.getStateList();
    }
    getCustomersByType(BOTH, response => {
      customer.current = response;
    }, mounted);
    if (!selectedCoverage && !allCoverageOptions) {
      getAppraiserActiveCoverage(handleAppraiserCoverage, mounted);
    }
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (props.stateList.length && !selectedStates.length) {
      // Select all states to start
      setSelectedStates(getStateOptions());
      triggerMapUpdate();
    }
  }, [props.stateList]);

  useEffect(() => {
    if (!selectedCounties.length) {
      setSelectedCoverage([]);
    } else {
      setSelectedCoverage(getCoverage());
    }
    triggerMapUpdate();
  }, [selectedCounties]);

  useEffect(() => {
    // Add all counties for selected states
    let coverage = getCoverage();
    // Id needs to be a string to work with the multiselect
    let selectedCountyIds = coverage?.map(c => `${c.id}`);
    setSelectedCounties(selectedCountyIds);
    setTimeout(() => setShowStateListPicker(false), 1000);
    setSelectedCoverage(coverage);
    triggerMapUpdate();
  }, [selectedStates]);

  const getCoverage = () => {
    if (!allCoverageOptions) return [];
    return allCoverageOptions?.filter(c => {
      if (selectedStates.length && !selectedStates.includes(c.stateAbbreviation)) return false;
      if (selectedCounties.length && !selectedCounties.includes(`${c.id}`)) return false;
      return true;
    });
  };

  const handleAppraiserCoverage = (response) => {
    let coverage = response.map(c => {
      return {
        id: c.countyId,
        hasCoverage: c.metrics.hasAppraiserCoverage,
        stateAbbreviation: c.stateAbbreviation,
        countyName: c.countyName,
      };
    });
    setAllCoverageOptions(coverage);
    setSelectedCoverage(coverage);
  };

  const triggerMapUpdate = () => {
    setLastMapUpdate(Date.now());
  };

  const setSelectedStateList = (data) => {
    let stateList = data.filter(d => d.checked).map(d => d.value);
    if (stateList.length) {
      setSelectedStates(stateList);
      setSelectedCounties([]);
    } else props.makeErrorToast("Please select at least one state.");
  };

  const getStateListDialog = () => {
    return (
      <MDialogSelectList
        title={"Select States to View"}
        data={getStateOptions()}
        onApply={setSelectedStateList}
        onClose={() => setShowStateListPicker(false)}
        showSelectAll
      />
    );
  };

  const getStateOptions = () => {
    return props.stateList?.map(s => (selectedStates && selectedStates.includes(s.stateAbbreviation))
      ? ({ checked: true, label: s.stateName, value: s.stateAbbreviation })
      : ({ checked: false, label: s.stateName, value: s.stateAbbreviation }));
  };

  const getCoutyOptions = () => {
    if (!selectedStates) {
      return props.allCountyOptions.sort(sortCounty);
    }
    return props.allCountyOptions.sort(sortCounty).filter(c => selectedStates.includes(c.state));
  };

  const countyTypeValueRenderer = (selected, options) => {
    if (selected.length === options.length && selected.length === props.allCountyOptions.length) return "All Counties Selected";
    if (selected.length) return `${selected.length} Counties Selected`;
    if (!selectedStates.length) return "Select states to filter counties";
    return "Filter Counties";
  };

  const onCountyFilterChanged = (selected) => {
    if (!selected.length) {
      setSelectedCounties([]);
      setSelectedCoverage([]);
    } else {
      setSelectedCounties(selected);
    }
  };

  const polygonMouseOver = (p) => {
    setMouseOverObject(p);
  };

  const polygonMouseOut = () => {
    setMouseOverObject(null);
  };

  const getMouseOverControl = (obj) => {
    if (obj && obj.type === "county") {
      // each item in countyBoundaries and selectedCoverage contain different info, so we need both to create the hover content 
      let county = props.countyBoundaries[obj.id].properties;
      let countyMatch = selectedCoverage.find(c => c.id === county.id);
      let fields = [];
      if (countyMatch.hasCoverage) {
        fields.push({
          key: "appraisal-services-present",
          dataType: DATA_TYPE_MULTI_LINE_STRING,
          label: " ",
          value: "Appraisal services are available in this area. Thank you for your inquiry.",
        });
      } else fields.push({
        key: "appraisal-services-not-present",
        dataType: DATA_TYPE_MULTI_LINE_STRING,
        label: " ",
        value: "Appraisal services are unavailable in this area at this time. We continue to expand into new areas and will share updates when available. Thank you for your inquiry.",
      });
      return {
        title: formatCounty(county.name, county.state),
        fields: fields,
      };
    }
  };

  const submitRequestAppraisalCoverage = (coverageRequests, additionalInfo) => {
    if (coverageRequests.length === 0) return props.makeErrorToast("Please select a county to submit your request.");
    let payload = {
      customerVolumeEstimateByCounty: coverageRequests.map(r => ({
        countyId: r.countyId,
        customerNumber: customer.current[0].number,
        estimatedOrdersMonthly: r.volume
      })),
      comments: additionalInfo
    };
    requestAppraisalCoverage(payload, submitCoverageCallback, mounted);
  };

  const submitCoverageCallback = () => {
    setShowRequestCoverageDialog(false);
    props.makeSuccessToast("Thank you for your request.");
  };

  const getRequestCoverageDialog = () => {
    if (props.stateList) {
      return (
        <MDialog
          id={"request-appraiser-coverage"}
          className={"request-appraiser-coverage-dialog"}
          title={"Request Appraisal Coverage"}
          visible={showRequestCoverageDialog}
          onHide={() => setShowRequestCoverageDialog(false)}
          focusOnMount={false}
          width={600}
        >
          <RequestCoverage 
            onSubmit={submitRequestAppraisalCoverage}
            onCancel={() => setShowRequestCoverageDialog(false)}
          />
        </MDialog>
      );
    }
  };

  if (!sentClickTrackingEmail.current && customer.current.length) {
    sentClickTrackingEmail.current = true;
    sendClickTrackingEmail(customer.current[0].number, null, mounted);
  }

  if (props.isFetchingCountyBoundaries || props.fetchingStateList) return (<MSpinner />);

  return (
    <div className={"appraiser-coverage-container"}>
      {showStateListPicker && getStateListDialog()}
      {showRequestCoverageDialog && getRequestCoverageDialog()}
      {props.stateList && props.allCountyOptions && (
        <FauxHeader2 title={"Appraiser Coverage Map"}>
          <div className={"appraiser-coverage-filters"}>
            <RMDButton onClick={() => setShowRequestCoverageDialog(true)}> {"Request Coverage"} </RMDButton>
            <RMDButton onClick={() => setShowStateListPicker(true)}>{"Filter States"}</RMDButton>
            <MultiSelect
              options={getCoutyOptions()}
              selected={selectedCounties}
              onSelectedChanged={onCountyFilterChanged}
              valueRenderer={countyTypeValueRenderer}
              disabled={!selectedStates.length}
              hasSelectAll
            />
          </div>
        </FauxHeader2>
      )}
      {mouseOverObject && <MToolTip visible={true} {...getMouseOverControl(mouseOverObject)} />}
      {props.countyBoundaries && selectedCoverage &&
        <>
          <Map
            containerClass="map-control"
            loadingClass="map-control"
            mapClass="map-control"
            manualUpdate
            lastUpdate={lastMapUpdate}
            streetView={false}
            {...mapArrayOfPolygons(selectedCoverage, props.countyBoundaries, polygonMouseOver, polygonMouseOut)}
          />
          <div className={"key"}>
            {showKey && <MCard
              title={"Key"}
              fields={[
                { key: "coverage", label: <div className={"coverage"} />, value: "Coverage", dataType: DATA_TYPE_JSX, className: "coverage" },
                { key: "no-coverage", label: <div className={"no-coverage"} />, value: "No coverage", dataType: DATA_TYPE_JSX, className: "no-coverage" },
                { key: "full-coverage", label: <div className={"full-coverage"} />, value: "State-wide coverage", dataType: DATA_TYPE_JSX, className: "full-coverage" },
              ]}
              actions={[
                {
                  icon: "close",
                  helpText: "Hide key",
                  onClick: () => setShowKey(false)
                }
              ]}
            />}
            {!showKey && <div className={"display-key-container"}>
              <Button
                secondary
                raised
                onClick={() => setShowKey(true)}
                className={"display-key-text"}>
                Show key
          </Button>
            </div>}
          </div>
        </>
      }
    </div>
  );
};

AppraiserCoverage.propTypes = {
  getCountyBoundaries: PropTypes.func.isRequired,
  isFetchingCountyBoundaries: PropTypes.bool.isRequired,
  countyBoundaries: PropTypes.object.isRequired,
  getStateList: PropTypes.func.isRequired,
  stateList: PropTypes.array.isRequired,
  makeErrorToast: PropTypes.func.isRequired,
  makeSuccessToast: PropTypes.func.isRequired,
  preferences: PropTypes.object.isRequired,
  fetchingStateList: PropTypes.bool.isRequired,
  allCountyOptions: PropTypes.array,
};

const mapStateToProps = state => ({
  isFetchingCountyBoundaries: state.region.isFetchingCountyBoundaries,
  countyBoundaries: state.region.countyBoundaries,
  allCountyOptions: allCountiesFromBoundaries(state),
  stateList: state.appraiser.stateList,
  fetchingStateList: state.appraiser.fetchingStateList,
  preferences: state.preferences,
});

const mapDispatchToProps = dispatch => ({
  getCountyBoundaries() {
    dispatch(getCountyBoundaries());
  },
  getStateList() {
    dispatch(getStateList());
  },
  makeErrorToast(text) {
    responseHandler.customToast(text, "error", dispatch);
  },
  makeSuccessToast(text) {
    responseHandler.customToast(text, "success", dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AppraiserCoverage);